import React, { useState, useEffect } from 'react';
import { useJobs } from '../../hooks/useJobs';
import RichTextEditor from './RichTextEditor';
import ImageUpload from './ImageUpload';
import './JobForm.css';

interface JobFormProps {
  job?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const disciplines = ['Accounting', 'Audit', 'Tax', 'Financial Management', 'Banking', 'Legal'];
const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Internship'];

const JobForm: React.FC<JobFormProps> = ({ job, onClose, onSuccess }) => {
  const { createJob, updateJob, refetch } = useJobs();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [autoSaved, setAutoSaved] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  const DRAFT_KEY = 'mk_job_form_draft';

  // Load saved draft or use defaults
  const getInitialFormData = () => {
    if (job) {
      // Editing existing job - don't load draft
      return {
        title_nl: job.title_nl || '',
        title_en: job.title_en || '',
        discipline: job.discipline || 'Accounting',
        location: job.location || '',
        salary: job.salary || '',
        description_nl: job.description_nl || '',
        description_en: job.description_en || '',
        posted_date: job.posted_date ? new Date(job.posted_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        deadline_date: job.deadline_date
          ? new Date(job.deadline_date).toISOString().split('T')[0]
          : (() => {
              const d = new Date();
              d.setMonth(d.getMonth() + 3);
              return d.toISOString().split('T')[0];
            })(),
        uploader_email: job.uploader_email || '',
        image_url: job.image_url || '',
        type: job.type || 'Full-time',
      };
    }

    // Creating new job - try to load draft
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate it has the right structure
        if (parsed.title_nl !== undefined) {
          // Check if draft actually has content
          const hasContent = parsed.title_nl || parsed.title_en || parsed.description_nl || parsed.description_en;
          if (hasContent) {
            setDraftLoaded(true);
          }
          return parsed;
        }
      }
    } catch (e) {
      console.error('Failed to load draft:', e);
    }

    // Default values
    return {
      title_nl: '',
      title_en: '',
      discipline: 'Accounting',
      location: '',
      salary: '',
      description_nl: '',
      description_en: '',
      posted_date: new Date().toISOString().split('T')[0],
      deadline_date: (() => {
        const d = new Date();
        d.setMonth(d.getMonth() + 3);
        return d.toISOString().split('T')[0];
      })(),
      uploader_email: '',
      image_url: '',
      type: 'Full-time',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Auto-save draft when form data changes (only for new jobs)
  useEffect(() => {
    if (!job) {
      // Debounce the save
      const timer = setTimeout(() => {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
          setAutoSaved(true);
          setTimeout(() => setAutoSaved(false), 2000);
        } catch (e) {
          console.error('Failed to save draft:', e);
        }
      }, 1000); // Save 1 second after user stops typing

      return () => clearTimeout(timer);
    }
  }, [formData, job]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (field: 'description_nl' | 'description_en', value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const clearDraft = () => {
    if (window.confirm('Are you sure you want to discard the saved draft and start fresh?')) {
      try {
        localStorage.removeItem(DRAFT_KEY);
        // Reset form to defaults
        const defaults = {
          title_nl: '',
          title_en: '',
          discipline: 'Accounting',
          location: '',
          salary: '',
          description_nl: '',
          description_en: '',
          posted_date: new Date().toISOString().split('T')[0],
          deadline_date: (() => {
            const d = new Date();
            d.setMonth(d.getMonth() + 3);
            return d.toISOString().split('T')[0];
          })(),
          uploader_email: '',
          image_url: '',
          type: 'Full-time',
        };
        setFormData(defaults);
        setDraftLoaded(false);
      } catch (e) {
        console.error('Failed to clear draft:', e);
      }
    }
  };

  // Helper function to check if HTML content is empty
  const isHtmlEmpty = (html: string): boolean => {
    if (!html) return true;
    // Remove HTML tags and whitespace
    const textContent = html.replace(/<[^>]*>/g, '').trim();
    return textContent.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate rich text content
    if (isHtmlEmpty(formData.description_nl)) {
      setError('Please enter a job description in Dutch');
      return;
    }
    if (isHtmlEmpty(formData.description_en)) {
      setError('Please enter a job description in English');
      return;
    }

    // Validate deadline date
    if (!formData.deadline_date || formData.deadline_date.trim() === '') {
      setError('Please enter a deadline date');
      return;
    }

    setLoading(true);

    try {
      if (job) {
        const { error } = await updateJob(job.id, formData);
        if (error) throw new Error(error);
        setSuccess('Job updated successfully!');
      } else {
        const { error } = await createJob(formData);
        if (error) throw new Error(error);
        setSuccess('Job created successfully!');

        // Clear the draft after successful creation
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch (e) {
          console.error('Failed to clear draft:', e);
        }
      }

      // Single refetch to ensure data is fresh
      await refetch();

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onSuccess();
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to save job');
      setLoading(false);
    }
  };

  return (
    <div className="job-form-overlay">
      <div className="job-form-modal">
        <div className="job-form-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2>{job ? 'Edit Job' : 'Create New Job'}</h2>
            {!job && autoSaved && (
              <span className="auto-save-indicator">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Draft saved
              </span>
            )}
          </div>
          <button onClick={onClose} className="job-form-close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          {draftLoaded && !job && (
            <div className="draft-loaded-banner">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span>Draft restored from previous session</span>
              </div>
              <button type="button" onClick={clearDraft} className="clear-draft-btn">
                Start Fresh
              </button>
            </div>
          )}

          {error && (
            <div className="job-form-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
              </svg>
              {error}
            </div>
          )}
          
          {success && (
            <div className="job-form-success" style={{ 
              padding: '1rem', 
              background: '#efe', 
              color: '#3c3', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              border: '1px solid #cfc',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <polyline points="9,12 12,15 15,9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {success}
            </div>
          )}

          <div className="job-form-grid">
            <div className="job-form-group">
              <label htmlFor="title_nl">Title (Dutch) *</label>
              <input
                type="text"
                id="title_nl"
                name="title_nl"
                value={formData.title_nl}
                onChange={handleChange}
                required
                placeholder="e.g., Senior Financial Controller"
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="title_en">Title (English) *</label>
              <input
                type="text"
                id="title_en"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                required
                placeholder="e.g., Senior Financial Controller"
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="discipline">Discipline *</label>
              <select
                id="discipline"
                name="discipline"
                value={formData.discipline}
                onChange={handleChange}
                required
              >
                {disciplines.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            <div className="job-form-group">
              <label htmlFor="location">Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="e.g., Amsterdam"
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="salary">Salary *</label>
              <input
                type="text"
                id="salary"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                required
                placeholder="e.g., €75,000 - €110,000"
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="type">Job Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                {jobTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="job-form-group">
              <label htmlFor="posted_date">Posted Date *</label>
              <input
                type="date"
                id="posted_date"
                name="posted_date"
                value={formData.posted_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="deadline_date">Deadline Date *</label>
              <input
                type="date"
                id="deadline_date"
                name="deadline_date"
                value={formData.deadline_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="job-form-group">
              <label htmlFor="uploader_email">Uploader Email *</label>
              <input
                type="email"
                id="uploader_email"
                name="uploader_email"
                value={formData.uploader_email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
              />
            </div>

            <div className="job-form-group full-width">
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData((prev: any) => ({ ...prev, image_url: url }))}
                label="Header Image (Optional)"
              />
            </div>
          </div>

          <div className="job-form-group full-width description-dutch">
            <label htmlFor="description_nl">Description (Dutch) *</label>
            <RichTextEditor
              value={formData.description_nl}
              onChange={(value) => handleDescriptionChange('description_nl', value)}
              placeholder="Enter job description in Dutch... Use the toolbar above to format your text (bold, italic, headings, lists, etc.)"
            />
          </div>

          <div className="job-form-group full-width">
            <label htmlFor="description_en">Description (English) *</label>
            <RichTextEditor
              value={formData.description_en}
              onChange={(value) => handleDescriptionChange('description_en', value)}
              placeholder="Enter job description in English... Use the toolbar above to format your text (bold, italic, headings, lists, etc.)"
            />
          </div>

          <div className="job-form-actions">
            <button type="button" onClick={onClose} className="job-form-btn-cancel">
              Cancel
            </button>
            <button type="submit" className="job-form-btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="mini-spinner"></div>
                  Saving...
                </>
              ) : (
                job ? 'Update Job' : 'Create Job'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;

